<template>
	<div v-loading="fullscreenLoading">
		<div class="company-payment-result">
			<div class="result-header" v-if="!isFromOrderList">
				<div class="result-icon">
					<i class="el-icon-circle-check ns-text-color"></i>
				</div>
				<div class="result-text">{{ getResultText() }}</div>
				<div class="result-desc" v-if="paymentType === 'company'">请按照以下信息进行对公转账</div>
				<div class="result-desc" v-else-if="paymentType === 'credits'">您的订单已使用授信额度支付完成</div>
			</div>
			
			<!-- 对公支付显示转账信息 -->
			<div class="transfer-info" v-if="paymentType === 'company'">
				<h3>转账信息</h3>
				<div class="info-item">
					<label>收款单位：</label>
					<span>{{ receivableInfo.bank_account_name || '' }}</span>
				</div>
				<div class="info-item">
					<label>开户银行：</label>
					<span>{{ receivableInfo.bank_name || '' }}</span>
				</div>
				<div class="info-item">
					<label>开户支行：</label>
					<span>{{ receivableInfo.bank_address || '' }}</span>
				</div>
				<div class="info-item">
					<label>银行账号：</label>
					<span>{{ receivableInfo.bank_account_no || '' }}</span>
				</div>
				<div class="info-item">
					<label>转账金额：</label>
					<span class="amount">￥{{ orderInfo.pay_money || '0.00' }}</span>
				</div>
				<div class="info-item">
					<label>订单编号：</label>
					<span>{{ orderInfo.order_no || orderCode || orderId }}</span>
				</div>
			</div>
			
			<!-- 授信支付显示订单信息 -->
			<div class="order-info" v-else-if="paymentType === 'credits'">
				<h3>订单信息</h3>
				<div class="info-item">
					<label>订单编号：</label>
					<span>{{ orderInfo.order_no || orderCode || orderId }}</span>
				</div>
				<div class="info-item">
					<label>支付金额：</label>
					<span class="amount">￥{{ orderInfo.pay_money || '0.00' }}</span>
				</div>
				<div class="info-item">
					<label>支付方式：</label>
					<span>授信额度支付</span>
				</div>
			</div>
			
			<!-- 对公支付上传凭证功能 -->
			<div class="upload-section" v-if="paymentType === 'company'">
				<h3>上传转账凭证</h3>
				<div class="upload-desc">请在完成转账后，上传转账凭证以便我们及时处理您的订单</div>
				
				<el-upload
					class="upload-demo"
					:action="uploadUrl"
					:headers="uploadHeaders"
					:data="uploadData"
					:on-success="handleUploadSuccess"
					:on-error="handleUploadError"
					:before-upload="beforeUpload"
					:file-list="fileList"
					:limit="1"
					list-type="picture-card"
					accept="image/*">
					<i class="el-icon-plus"></i>
					<div slot="tip" class="el-upload__tip">只能上传jpg/png文件，且不超过2MB</div>
				</el-upload>
				
				<div class="submit-voucher-section" v-if="fileList.length > 0">
					<el-button type="primary" size="large" @click="submitTransferVoucher" :loading="submitting">
						提交凭证
					</el-button>
				</div>
			</div>
			
			<div class="result-footer">
				<el-button @click="goToOrderList">返回订单列表</el-button>
				<el-button type="primary" @click="goHome">返回首页</el-button>
			</div>
		</div>
	</div>
</template>

<script>
import { apiOrderDetail } from '@/api/order/order';
import { uploadVoucher } from '@/api/upload';
import http from '@/utils/http';
import Config from '@/utils/config';

export default {
	name: 'company_payment_result',
	data() {
		return {
			orderId: '',
			orderCode: '',
			orderInfo: {},
			receivableInfo: {},
			fullscreenLoading: true,
			fileList: [],
			submitting: false,
			isFromOrderList: false, // 是否从订单列表跳转而来
			paymentType: 'company', // 支付类型：company-对公支付，credits-授信支付
			uploadUrl: Config.baseUrl + '/api/upload/shopimg',
			uploadHeaders: {},
			uploadData: {
				token: this.$store.getters.token || '',
				path: 'shopimg',
			}
		};
	},
	created() {
		// 检查是否从订单列表跳转而来
		this.isFromOrderList = this.$route.query.from === 'orderlist';
		
		// 获取支付类型
		this.paymentType = this.$route.query.type || 'company';
		
		// 支持两种参数：code（订单编号）或 order_id（订单ID）
		if (this.$route.query.order_id) {
			this.orderId = this.$route.query.order_id;
		} else if (this.$route.query.code) {
			this.orderCode = this.$route.query.code;
		} else {
			this.$router.push({ path: '/' });
			return;
		}
		this.fetchOrderInfo();
		// 只有对公支付才需要获取收款信息
		if (this.paymentType === 'company') {
			this.fetchReceivableInfo();
		}
	},
	methods: {
		getResultText() {
			if (this.paymentType === 'credits') {
				return '支付成功';
			} else {
				return '提交成功';
			}
		},
		
		async fetchOrderInfo() {
			try {
				const params = {
					order_id: this.orderId || '',
					order_code: this.orderCode || '',
				}
				const response = await apiOrderDetail(params);
				if (response.code === 0) {  // 修改为 0，因为API成功返回的code是0
					this.orderInfo = response.data;
				} else {
					// 如果获取失败，使用模拟数据
					this.orderInfo = {
						order_no: this.orderCode || this.orderId,
						pay_money: ''
					};
				}
			} catch (error) {
				console.error('获取订单信息失败:', error);
				// 使用模拟数据
				this.orderInfo = {
					order_no: this.orderCode || this.orderId,
					pay_money: ''
				};
			} finally {
				this.fullscreenLoading = false;
			}
		},
		
		async fetchReceivableInfo() {
			try {
				// 使用项目的HTTP工具函数
				const response = await http({
					url: '/api/order/receivable',
					data: {
						order_id: this.orderId
					}
				});
				if (response.code === 0 && response.data && response.data.value) {
					this.receivableInfo = response.data.value;
				}
			} catch (error) {
				console.error('获取收款信息失败:', error);
				// 使用默认收款信息
				this.receivableInfo = {
					bank_account_name: '广东触角科技有限公司',
					bank_name: '中国工商银行',
					bank_address: '广州分行',
					bank_account_no: '3602000109200123456'
				};
			}
		},
		
		beforeUpload(file) {
			// 更新token
			this.uploadData.token = this.$store.getters.token || '';
			
			const isImage = file.type.indexOf('image/') === 0;
			const isLt2M = file.size / 1024 / 1024 < 2;

			if (!isImage) {
				this.$message.error('只能上传图片文件!');
				return false;
			}
			if (!isLt2M) {
				this.$message.error('上传图片大小不能超过 2MB!');
				return false;
			}
			return true;
		},
		
		handleUploadSuccess(response, file, fileList) {
			if (response.code >= 0) {
				this.$message.success('转账凭证上传成功！');
				// 更新文件列表，保存上传的图片信息
				this.fileList = fileList;
			} else {
				this.$message.error(response.message || '上传失败');
			}
		},
		
		// 上传转账凭证
		async handleUpload() {
			if (!this.selectedFile) {
				this.$message.error('请选择要上传的图片');
				return;
			}

			const formData = new FormData();
			formData.append('file', this.selectedFile);

			this.uploading = true;
			try {
				const response = await uploadVoucher(formData);
				if (response.code === 1) {
					this.uploadedImage = response.data.pic_path;
					this.$message.success('上传成功');
				} else {
					this.$message.error(response.message || '上传失败');
				}
			} catch (error) {
				console.error('上传失败:', error);
				this.$message.error('上传失败，请重试');
			} finally {
				this.uploading = false;
			}
		},
		
		handleUploadError(err, file, fileList) {
			this.$message.error('上传失败，请重试');
		},
		
		async submitTransferVoucher() {
			if (this.fileList.length === 0) {
				this.$message.error('请先上传转账凭证');
				return;
			}
			
			this.submitting = true;
			try {
				// 获取上传的图片完整路径，使用pic_path字段
				const imageUrl = this.fileList[0].response?.data?.pic_path || this.fileList[0].url;
				
				const params = {
					order_id: this.orderInfo.order_id || this.orderId,
					voucherImage: imageUrl
				};
				
				const response = await http({
					url: '/api/order/uploadVoucher',
					method: 'post',
					data: params
				});
				
				if (response.code === 0) {
					this.$message.success('转账凭证提交成功！');
					// 跳转到订单列表
					setTimeout(() => {
						this.goToOrderList();
					}, 1500);
				} else {
					this.$message.error(response.message || '提交失败');
				}
			} catch (error) {
				console.error('提交转账凭证失败:', error);
				this.$message.error('网络错误，请重试');
			} finally {
				this.submitting = false;
			}
		},
		
		goToOrderList() {
			this.$router.push({ path: '/member/order_list' });
		},
		
		goHome() {
			this.$router.push({ path: '/' });
		}
	}
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/main.scss";

.company-payment-result {
	max-width: 800px;
	margin: 20px auto;
	padding: 30px;
	background: #ffffff;
	border-radius: 8px;
	box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.result-header {
	text-align: center;
	margin-bottom: 40px;
	
	.result-icon {
		i {
			font-size: 65px;
			color: #67c23a;
		}
	}
	
	.result-text {
		font-size: 24px;
		font-weight: bold;
		margin: 15px 0 10px;
		color: #303133;
	}
	
	.result-desc {
		font-size: 16px;
		color: #606266;
	}
}

.transfer-info {
	margin-bottom: 40px;
	padding: 20px;
	background: #f8f9fa;
	border-radius: 6px;
	
	h3 {
		margin: 0 0 20px 0;
		font-size: 18px;
		color: #303133;
		border-bottom: 2px solid #409eff;
		padding-bottom: 10px;
	}
	
	.info-item {
		display: flex;
		margin-bottom: 15px;
		font-size: 16px;
		
		label {
			width: 120px;
			color: #606266;
			font-weight: bold;
		}
		
		span {
			color: #303133;
			
			&.amount {
				color: #e6a23c;
				font-weight: bold;
				font-size: 18px;
			}
		}
	}
}

.upload-section {
	margin-bottom: 40px;
	
	h3 {
		margin: 0 0 15px 0;
		font-size: 18px;
		color: #303133;
		border-bottom: 2px solid #409eff;
		padding-bottom: 10px;
	}
	
	.upload-desc {
		margin-bottom: 20px;
		color: #606266;
		font-size: 14px;
	}
	
	.submit-voucher-section {
		margin-top: 20px;
		text-align: center;
		
		.el-button {
			padding: 12px 40px;
			font-size: 16px;
		}
	}
}

.result-footer {
	text-align: center;
	
	.el-button {
		margin: 0 10px;
		padding: 12px 30px;
	}
}

::v-deep .el-upload--picture-card {
	width: 120px;
	height: 120px;
	line-height: 120px;
}

::v-deep .el-upload-list--picture-card .el-upload-list__item {
	width: 120px;
	height: 120px;
}
</style>